import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as userSelectors from 'selectors/user';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import FormSection from 'components/templates/FormSection';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import Errors from 'components/shared/Errors';
import DeleteAccount from 'modules/users/containers/delete-account';
import ConfirmPasswordDialog from 'modules/users/components/confirm-password-dialog';
import BankIdLoginDialog from 'modules/users/components/bankid-login-dialog';
import * as actions from 'actions';
import ProfileTabs from 'modules/users/components/ProfileTabs';
import ProfileForm from 'modules/users/components/ProfileForm';
import SCAVerificationDialog from 'components/shared/SCAVerificationDialog';
import { shouldTriggerSCA } from 'logic/sca';

const styles = makeStyles((theme) => ({
  changepwd: {
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
    },
  },
}));

const Profile = () => {
  const classes = styles();
  const [panels, setPanels] = React.useState({
    basic: true,
    password: false,
  });
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.userData);
  const canChangeName = useSelector(userSelectors.canChangeName);
  const updateState = useSelector(userSelectors.updateState);
  const success = useSelector(userSelectors.success);
  const submitErrors = useSelector(userSelectors.errors);
  const [open, setOpen] = React.useState(false);
  const [openBankid, setOpenBankid] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState(null);

  const onSubmit = async (values = submittedData) => {
    const triggerSCA = shouldTriggerSCA(values, user);
    dispatch(actions.updateUserRequest({ ...values, triggerSCA }));
  };

  const onPreSubmit = (values) => {
    if (user.type === 'STRICT' && !user.bankid_verified_at) {
      setSubmittedData(values);

      return setOpen(true);
    }

    if (user.type === 'STRICT' && user.bankid_verified_at && !user.bankid_session) {
      setSubmittedData(values);

      return setOpenBankid(true);
    }

    return onSubmit(values);
  };

  React.useEffect(() => {
    if (user.type === 'SOFT') {
      setPanels({
        basic: true, password: true,
      });
    }
  }, [user.type]);

  React.useEffect(() => {
    if (updateState === 2) {
      setSubmittedData(null);
    }

    if (updateState === 2 && location.search) {
      const nextRegex = /^\?next=([/0-9a-z]+)/ig;
      const matchArr = nextRegex.exec(location.search.replace(/%2F/g, '/'));
      if (matchArr && matchArr.length >= 2) {
        history.push(matchArr[1]);
      }
    }
  }, [updateState]);

  return (
    <Main title={(
      <FormattedMessage
        id="userform.myProfile"
        defaultMessage="My profile"
      />
    )}
    >
      <ProfileTabs
        tab={0}
        bookingActive={!!(user.has_reviewed_cottages || user.dac7_valid)}
      />
      <BankIdLoginDialog
        open={openBankid}
        header={(
          <FormattedMessage
            id="bankid.login"
            defaultMessage="Login with BankID"
          />
        )}
        title={(
          <Box textAlign="center">
            <FormattedMessage
              id="bankid.security"
              defaultMessage="For security reasons, please log in with your BankID before continuing"
            />
          </Box>
        )}
        onSuccess={() => {
          setOpenBankid(false);
          onSubmit(submittedData);
        }}
        onClose={() => setOpenBankid(false)}
      />
      <ConfirmPasswordDialog
        onClose={() => setOpen(false)}
        open={open}
        onSuccess={(password) => {
          setOpen(false);

          return onSubmit({
            ...submittedData,
            password,
          });
        }}
      />

      <SCAVerificationDialog />

      <FormSection clear>
        <ProfileForm
          canChangeName={canChangeName}
          onSubmit={onPreSubmit}
          panels={panels}
          initialValues={{ ...user }}
        />
        <hr />
        <Box py={2} textAlign="right">
          {
            user.type === 'STRICT' && (
              <Typography variant="body2" component="span" className={classes.changepwd}>
                <Link to="/change-password">
                  <FormattedMessage
                    id="userform.changePassword"
                    defaultMessage="Change password"
                  />
                </Link>
              </Typography>
            )
          }
          &nbsp;&nbsp;&nbsp;
          <DeleteAccount />
        </Box>
      </FormSection>

      <Errors errors={submitErrors} />
      <Success success={success} />
    </Main>
  );
};

export default Profile;
